/**
 * Backup Mongo → R2
 * Configuração do cron diário de backup dos clusters MongoDB do ecossistema.
 * Restrito a super_admin.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add, Delete, Edit, PlayArrow, ExpandMore, ExpandLess } from '@mui/icons-material';
import { format } from 'date-fns';
import { apiService } from '../services/api';
import { useConfirmation } from '../contexts/ConfirmationContext';

const SOURCE_ENV_VARS = ['MONGODB_URI', 'MONGO_SOURCE_VBODY', 'MONGO_SOURCE_VCONDO'] as const;

/** Sugestões de preenchimento ao criar uma config nova para cada fonte — puramente UX,
 * o backend deriva o banco (mongodump --db) sozinho a partir da fonte escolhida. */
const SOURCE_SUGGESTIONS: Record<
  (typeof SOURCE_ENV_VARS)[number],
  { label: string; r2Directory: string }
> = {
  MONGODB_URI: { label: 'Vover Control Plane', r2Directory: 'control_plane' },
  MONGO_SOURCE_VBODY: { label: 'iBody', r2Directory: 'vbody' },
  MONGO_SOURCE_VCONDO: { label: 'Vover App', r2Directory: 'vcondo' }
};

const COLUMN_COUNT = 8;

interface MongoBackupConfig {
  _id: string;
  sourceEnvVar: string;
  label: string;
  r2Directory: string;
  cronExpression: string;
  retentionCount: number;
  enabled: boolean;
  lastRunAt?: string;
  lastRunStatus?: 'running' | 'success' | 'error';
  lastRunPhase?: 'dumping' | 'uploading' | 'pruning';
  lastRunProgressPercent?: number;
  lastRunError?: string;
  lastRunKey?: string;
  lastRunSizeBytes?: number;
  lastRunDurationMs?: number;
}

interface MongoBackupRun {
  _id: string;
  status: 'success' | 'error';
  key?: string;
  sizeBytes?: number;
  durationMs: number;
  error?: string;
  trigger: 'schedule' | 'manual';
  startedAt: string;
  finishedAt: string;
}

function formatBytes(bytes?: number): string {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}

function formatDuration(ms?: number): string {
  if (!ms && ms !== 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'dd/MM/yyyy HH:mm:ss');
  } catch {
    return iso;
  }
}

const PHASE_LABELS: Record<'dumping' | 'uploading' | 'pruning', string> = {
  dumping: 'Copiando dados do Mongo…',
  uploading: 'Enviando para o R2…',
  pruning: 'Aplicando retenção…'
};

function LastRunCell({ cfg }: { cfg: MongoBackupConfig }) {
  if (cfg.lastRunStatus === 'running') {
    const phase = cfg.lastRunPhase ?? 'dumping';
    const percent = phase === 'dumping' ? cfg.lastRunProgressPercent : undefined;
    return (
      <Stack spacing={0.5} sx={{ minWidth: 170 }}>
        <Chip size="small" color="warning" label="Executando…" />
        {typeof percent === 'number' ? (
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{ borderRadius: 1, height: 6 }}
          />
        ) : (
          <LinearProgress variant="indeterminate" sx={{ borderRadius: 1, height: 6 }} />
        )}
        <Typography variant="caption" color="text.secondary">
          {PHASE_LABELS[phase]}
          {typeof percent === 'number' ? ` ${percent.toFixed(0)}%` : ''}
        </Typography>
      </Stack>
    );
  }

  const chip =
    cfg.lastRunStatus === 'success' ? (
      <Chip size="small" color="success" label="Sucesso" />
    ) : cfg.lastRunStatus === 'error' ? (
      <Chip size="small" color="error" label="Falhou" />
    ) : (
      <Chip size="small" variant="outlined" label="Nunca executado" />
    );

  return (
    <Stack spacing={0.25}>
      {cfg.lastRunStatus === 'error' && cfg.lastRunError ? (
        <Tooltip title={cfg.lastRunError}>{chip}</Tooltip>
      ) : (
        chip
      )}
      <Typography variant="caption" color="text.secondary">
        {formatDate(cfg.lastRunAt)}
      </Typography>
    </Stack>
  );
}

/**
 * Linha de histórico, sempre renderizada como <TableRow> irmã da linha principal
 * (nunca aninhada dentro de uma <TableCell>/<Stack> — isso quebraria o DOM da
 * tabela). A visibilidade é controlada por Collapse + `open`, e os dados só são
 * buscados na primeira expansão.
 */
interface LogDialogState {
  loading: boolean;
  log: string | null;
  error: string | null;
}

const BackupRunHistoryRow: React.FC<{ configId: string; open: boolean }> = ({ configId, open }) => {
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState<MongoBackupRun[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logDialog, setLogDialog] = useState<LogDialogState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getMongoBackupRuns(configId, 10);
      setRuns((res.data?.data ?? []) as MongoBackupRun[]);
      setLoaded(true);
    } catch {
      setError('Não foi possível carregar o histórico.');
    } finally {
      setLoading(false);
    }
  }, [configId]);

  useEffect(() => {
    if (open && !loaded) void load();
  }, [open, loaded, load]);

  const openLog = async (runId: string) => {
    setLogDialog({ loading: true, log: null, error: null });
    try {
      const res = await apiService.getMongoBackupRunLog(configId, runId);
      setLogDialog({
        loading: false,
        log: (res.data?.data?.log as string | null) ?? null,
        error: null
      });
    } catch (e: any) {
      setLogDialog({
        loading: false,
        log: null,
        error: e?.response?.data?.message || 'Não foi possível carregar o log desta execução.'
      });
    }
  };

  const closeLog = () => setLogDialog(null);

  return (
    <TableRow>
      <TableCell colSpan={COLUMN_COUNT} sx={{ py: 0, borderBottom: open ? undefined : 0 }}>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ py: 1.5 }}>
            {loading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="caption">Carregando histórico…</Typography>
              </Stack>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {error}
              </Alert>
            )}
            {!loading && !error && runs.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Nenhuma execução registrada ainda.
              </Typography>
            )}
            {!loading && runs.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Início</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tamanho</TableCell>
                    <TableCell>Duração</TableCell>
                    <TableCell>Detalhe</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map(run => (
                    <TableRow key={run._id}>
                      <TableCell>{formatDate(run.startedAt)}</TableCell>
                      <TableCell>{run.trigger === 'manual' ? 'Manual' : 'Agendado'}</TableCell>
                      <TableCell>
                        {run.status === 'success' ? (
                          <Chip size="small" color="success" label="Sucesso" />
                        ) : (
                          <Chip size="small" color="error" label="Falhou" />
                        )}
                      </TableCell>
                      <TableCell>{formatBytes(run.sizeBytes)}</TableCell>
                      <TableCell>{formatDuration(run.durationMs)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25} alignItems="flex-start">
                          <Typography
                            variant="caption"
                            color={run.status === 'error' ? 'error.main' : 'text.secondary'}
                            sx={{ wordBreak: 'break-all' }}
                          >
                            {run.status === 'error' ? run.error : run.key}
                          </Typography>
                          {run.status === 'error' && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => void openLog(run._id)}
                              sx={{ minWidth: 0, p: 0, textTransform: 'none' }}
                            >
                              Ver log completo
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Collapse>
      </TableCell>

      <Dialog open={Boolean(logDialog)} onClose={closeLog} maxWidth="md" fullWidth>
        <DialogTitle>Log completo da execução</DialogTitle>
        <DialogContent dividers>
          {logDialog?.loading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2">Carregando log…</Typography>
            </Stack>
          )}
          {logDialog?.error && <Alert severity="error">{logDialog.error}</Alert>}
          {logDialog && !logDialog.loading && !logDialog.error && (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: 12,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                maxHeight: '60vh',
                overflow: 'auto'
              }}
            >
              {logDialog.log || 'Log não disponível para esta execução.'}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLog}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
};

const MongoBackupConfigPage: React.FC = () => {
  const { confirm } = useConfirmation();
  const [configs, setConfigs] = useState<MongoBackupConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleHistory = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [editing, setEditing] = useState<MongoBackupConfig | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    sourceEnvVar: SOURCE_ENV_VARS[0] as string,
    label: '',
    r2Directory: '',
    cronExpression: '0 23 * * *',
    retentionCount: '5'
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dialogOpen = Boolean(editing) || creating;

  const usedSources = new Set(configs.map(c => c.sourceEnvVar));
  const availableSources = SOURCE_ENV_VARS.filter(s => !usedSources.has(s));

  /** Busca as configs sem mexer no spinner de página inteira — usado pelo polling
   * enquanto algum backup está "running", para não piscar a tabela a cada 2s. */
  const fetchConfigs = useCallback(async () => {
    try {
      const res = await apiService.getMongoBackupConfigs();
      setConfigs((res.data?.data ?? []) as MongoBackupConfig[]);
    } catch (e: any) {
      setMessage({
        type: 'error',
        text: e?.response?.data?.message || 'Não foi possível carregar as configurações de backup.'
      });
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    await fetchConfigs();
    setLoading(false);
  }, [fetchConfigs]);

  useEffect(() => {
    void load();
  }, [load]);

  // Enquanto alguma config estiver "running", faz polling silencioso para
  // acompanhar o progresso (percentual do dump, fase atual) até terminar.
  useEffect(() => {
    const anyRunning = configs.some(c => c.lastRunStatus === 'running');
    if (!anyRunning) return undefined;
    const interval = setInterval(() => {
      void fetchConfigs();
    }, 2000);
    return () => clearInterval(interval);
  }, [configs, fetchConfigs]);

  const openEdit = (cfg: MongoBackupConfig) => {
    setCreating(false);
    setEditing(cfg);
    setForm({
      sourceEnvVar: cfg.sourceEnvVar,
      label: cfg.label,
      r2Directory: cfg.r2Directory,
      cronExpression: cfg.cronExpression,
      retentionCount: String(cfg.retentionCount)
    });
    setFormError(null);
  };

  const openCreate = () => {
    const first = availableSources[0];
    if (!first) return;
    const suggestion = SOURCE_SUGGESTIONS[first];
    setEditing(null);
    setCreating(true);
    setForm({
      sourceEnvVar: first,
      label: suggestion.label,
      r2Directory: suggestion.r2Directory,
      cronExpression: '0 23 * * *',
      retentionCount: '5'
    });
    setFormError(null);
  };

  const closeDialog = () => {
    setEditing(null);
    setCreating(false);
    setFormError(null);
  };

  /** Ao trocar a Fonte enquanto cria (não em edição), sugere produto/diretório coerentes. */
  const handleSourceChange = (sourceEnvVar: string) => {
    setForm(f => {
      if (!creating) return { ...f, sourceEnvVar };
      const suggestion = SOURCE_SUGGESTIONS[sourceEnvVar as (typeof SOURCE_ENV_VARS)[number]];
      return suggestion
        ? { ...f, sourceEnvVar, label: suggestion.label, r2Directory: suggestion.r2Directory }
        : { ...f, sourceEnvVar };
    });
  };

  const handleSave = async () => {
    const retentionCount = parseInt(form.retentionCount, 10);
    if (!Number.isFinite(retentionCount) || retentionCount < 1 || retentionCount > 50) {
      setFormError('Retenção deve ser um número inteiro entre 1 e 50.');
      return;
    }
    setSaving(true);
    setFormError(null);
    const payload = {
      sourceEnvVar: form.sourceEnvVar,
      label: form.label.trim(),
      r2Directory: form.r2Directory.trim(),
      cronExpression: form.cronExpression.trim(),
      retentionCount
    };
    try {
      if (creating) {
        await apiService.createMongoBackupConfig(payload);
        setMessage({ type: 'success', text: 'Configuração de backup criada.' });
      } else if (editing) {
        await apiService.updateMongoBackupConfig(editing._id, payload);
        setMessage({ type: 'success', text: 'Configuração de backup atualizada.' });
      }
      closeDialog();
      await load();
    } catch (e: any) {
      setFormError(e?.response?.data?.message || 'Falha ao salvar configuração.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (cfg: MongoBackupConfig) => {
    confirm({
      title: 'Remover configuração de backup',
      message: `Deseja remover a configuração "${cfg.label}" (${cfg.sourceEnvVar})? O histórico de execuções dela também será apagado. Os arquivos já enviados ao R2 não são afetados.`,
      confirmText: 'Remover',
      cancelText: 'Cancelar',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await apiService.deleteMongoBackupConfig(cfg._id);
          setMessage({ type: 'success', text: 'Configuração de backup removida.' });
          await load();
        } catch (e: any) {
          setMessage({
            type: 'error',
            text: e?.response?.data?.message || 'Falha ao remover configuração.'
          });
        }
      }
    });
  };

  const toggleEnabled = async (cfg: MongoBackupConfig) => {
    try {
      await apiService.updateMongoBackupConfig(cfg._id, { enabled: !cfg.enabled });
      await load();
    } catch (e: any) {
      setMessage({
        type: 'error',
        text: e?.response?.data?.message || 'Falha ao atualizar status da configuração.'
      });
    }
  };

  const runNow = async (cfg: MongoBackupConfig) => {
    // Guarda local só pro intervalo entre o clique e a primeira confirmação do
    // servidor — depois disso, quem manda é cfg.lastRunStatus (polling real).
    setRunningIds(prev => new Set(prev).add(cfg._id));
    try {
      await apiService.runMongoBackupNow(cfg._id);
      setMessage({
        type: 'success',
        text: `Backup de "${cfg.label}" iniciado — acompanhe o progresso abaixo.`
      });
      setTimeout(() => void fetchConfigs(), 500);
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Falha ao iniciar backup.' });
    } finally {
      setTimeout(() => {
        setRunningIds(prev => {
          const next = new Set(prev);
          next.delete(cfg._id);
          return next;
        });
      }, 2000);
    }
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={20} />
            <Typography>Carregando configurações de backup…</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      <Card variant="outlined" sx={{ borderColor: 'primary.light', bgcolor: 'action.hover' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Backup Mongo → R2
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Um job por fonte. O ambiente (staging ou produção) é definido pelo deploy do
                  vover-admin, não por esta tela — cada job roda no horário definido abaixo, gera o
                  dump via mongodump e envia para o diretório indicado no bucket de backup, mantendo
                  apenas os últimos N arquivos dessa fonte.
                </Typography>
              </Box>
              <Tooltip
                title={availableSources.length === 0 ? 'As 3 fontes já têm configuração' : ''}
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openCreate}
                    disabled={availableSources.length === 0}
                  >
                    Nova configuração
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="primary" label="Restrito a super_admin" />
              <Chip
                size="small"
                variant="outlined"
                label="Credenciais dos clusters ficam só em env vars"
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fonte</TableCell>
                <TableCell>Produto</TableCell>
                <TableCell>Diretório R2</TableCell>
                <TableCell>Cron</TableCell>
                <TableCell>Retenção</TableCell>
                <TableCell>Habilitado</TableCell>
                <TableCell>Última execução</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map(cfg => (
                <React.Fragment key={cfg._id}>
                  <TableRow>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{cfg.sourceEnvVar}</TableCell>
                    <TableCell>{cfg.label}</TableCell>
                    <TableCell>{cfg.r2Directory}/</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{cfg.cronExpression}</TableCell>
                    <TableCell>{cfg.retentionCount}</TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={cfg.enabled}
                        onChange={() => void toggleEnabled(cfg)}
                      />
                    </TableCell>
                    <TableCell>
                      <LastRunCell cfg={cfg} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Executar agora">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => void runNow(cfg)}
                              disabled={runningIds.has(cfg._id) || cfg.lastRunStatus === 'running'}
                            >
                              <PlayArrow fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(cfg)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ver histórico">
                          <IconButton size="small" onClick={() => toggleHistory(cfg._id)}>
                            {expandedIds.has(cfg._id) ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={() => handleDelete(cfg)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <BackupRunHistoryRow configId={cfg._id} open={expandedIds.has(cfg._id)} />
                </React.Fragment>
              ))}
              {configs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma configuração de backup encontrada.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {creating ? 'Nova configuração de backup' : `Editar configuração — ${editing?.label}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              select
              label="Fonte"
              value={form.sourceEnvVar}
              onChange={e => handleSourceChange(e.target.value)}
              fullWidth
              helperText="Env var que guarda a connection string do cluster de origem."
            >
              {(creating ? availableSources : SOURCE_ENV_VARS).map(envVar => (
                <MenuItem key={envVar} value={envVar}>
                  {envVar}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Produto"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              fullWidth
              helperText="Texto livre para identificar do que se trata este backup."
            />
            <TextField
              label="Diretório no R2"
              value={form.r2Directory}
              onChange={e => setForm(f => ({ ...f, r2Directory: e.target.value }))}
              fullWidth
              helperText="Criado automaticamente no bucket se ainda não existir."
            />
            <TextField
              label="Expressão cron"
              value={form.cronExpression}
              onChange={e => setForm(f => ({ ...f, cronExpression: e.target.value }))}
              fullWidth
              helperText="Formato crontab (5 campos, horário America/Sao_Paulo). Padrão: 0 23 * * * (todo dia às 23h)."
            />
            <TextField
              label="Retenção (quantos backups manter)"
              type="number"
              value={form.retentionCount}
              onChange={e => setForm(f => ({ ...f, retentionCount: e.target.value }))}
              fullWidth
              inputProps={{ min: 1, max: 50 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Salvando…' : creating ? 'Criar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MongoBackupConfigPage;
